<template>
  <section
    v-if="lines.length"
    class="flex flex-col gap-1.5 rounded-lg border border-lightGray30 bg-white p-3"
    aria-label="Prazos do SLA"
    :data-testid="testId"
  >
    <div class="flex flex-wrap justify-between gap-x-3 gap-y-1 text-[11px] text-neutral50">
      <span class="uppercase tracking-wider">Prazos · horário de Brasília</span>
      <span>clique numa linha para ver só aqueles plantões</span>
    </div>
    <button
      v-for="line in lines"
      :key="line.band"
      type="button"
      class="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-1.5 rounded-md border px-3 py-2 text-left md:grid-cols-[minmax(130px,auto)_1fr_minmax(200px,280px)]"
      :class="[
        LINE_CLASS[line.state],
        activeBand === line.band
          ? 'border-primary shadow-[inset_0_0_0_1px_theme(colors.primary)]'
          : 'border-lightGray30'
      ]"
      :aria-pressed="activeBand === line.band"
      :data-state="line.state"
      :data-testid="`${testId}-line-${line.band}`"
      @click="emit('toggle-band', line.band)"
    >
      <span
        class="font-mono text-2xl font-bold tabular-nums"
        :class="PRIORITY_SLA_TEXT_CLASS[line.state]"
        :data-testid="`${testId}-line-${line.band}-clock`"
      >
        {{ line.clock }}
      </span>
      <span class="flex min-w-0 flex-col">
        <span class="text-sm font-semibold text-textPrimary">{{ line.title }}</span>
        <span
          class="text-xs text-neutral50"
          :data-testid="`${testId}-line-${line.band}-description`"
        >
          {{ line.description }}
        </span>
      </span>
      <span class="col-span-2 flex flex-col gap-1 md:col-span-1">
        <span
          class="flex h-1.5 overflow-hidden rounded-[3px] bg-lightGray30"
          role="img"
          :aria-label="`${line.done} de ${line.total} cumpridos`"
        >
          <i
            v-for="(segment, index) in line.segments"
            :key="index"
            class="block h-full"
            :class="SEGMENT_CLASS[segment.tone]"
            :style="{ width: `${line.total ? (segment.count / line.total) * 100 : 0}%` }"
          />
        </span>
        <span
          class="text-[11px] text-neutral50"
          :data-testid="`${testId}-line-${line.band}-progress`"
        >
          {{ line.progress }}
        </span>
      </span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type {
  PriorityBandsDto,
  PriorityConfig,
  PrioritySlaBand,
  PrioritySlaState,
  PriorityTab
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { buildPrioritySlaClockLines } from '../slaClock'
import type { PrioritySlaClockTone } from '../slaClock'
import { PRIORITY_SLA_TEXT_CLASS } from '../slaFormat'

/**
 * Relógio em linhas (M3): uma linha por prazo, countdown grande, quantos
 * vencem nele e a barra de cumpridos. Clicar liga/desliga `slaBand` (a página
 * decide; aqui só se emite). Sem `aria-live`: o countdown muda todo segundo.
 */
const props = withDefaults(
  defineProps<{
    bands: PriorityBandsDto | null
    tab: PriorityTab
    activeBand: PrioritySlaBand | null
    nowMs: number | null
    config: PriorityConfig | null
    testId?: string
  }>(),
  { testId: 'priority-sla-clock' }
)

const emit = defineEmits<{ 'toggle-band': [band: PrioritySlaBand] }>()

const lines = computed(() =>
  buildPrioritySlaClockLines(props.bands, props.tab, props.nowMs, props.config)
)

const LINE_CLASS: Record<PrioritySlaState, string> = {
  OVERDUE: 'bg-red-50',
  URGENT: 'bg-orange-50',
  ATTENTION: 'bg-amber-50',
  ON_TIME: 'bg-white',
  DONE: 'bg-white',
  NOT_STARTED: 'bg-white'
}

const SEGMENT_CLASS: Record<PrioritySlaClockTone, string> = {
  ok: 'bg-green-600',
  info: 'bg-blue-500',
  crit: 'bg-red-600',
  warn: 'bg-orange-500',
  mute: 'bg-neutral50/30'
}
</script>
